import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Bot, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'tababa_ai_session_id';
const POS_KEY = 'tababa_ai_bot_position';

// Load saved FAB position or default to bottom-right (works for LTR & RTL).
const loadSavedPosition = () => {
  try {
    const raw = window.localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.x === 'number' && typeof p?.y === 'number') return p;
  } catch (_e) { /* corrupt storage */ }
  return null;
};

/**
 * Floating AI concierge shown on the public home page. Answers questions
 * about doctor availability, packages, and how to book. Grounded in a
 * live DB snapshot on the backend so it cannot hallucinate a doctor.
 */
export const AIBotWidget = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(
    typeof window !== 'undefined' ? window.localStorage.getItem(SESSION_KEY) : null
  );

  // FAB position — null means "use CSS default (bottom-end corner)". Once
  // the user drags it we switch to explicit {x, y} coords.
  const [pos, setPos] = useState(() =>
    (typeof window !== 'undefined' ? loadSavedPosition() : null)
  );
  const dragState = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
  });

  const beginDrag = (clientX, clientY) => {
    const rect = { x: pos?.x ?? window.innerWidth - 80, y: pos?.y ?? window.innerHeight - 80 };
    dragState.current = {
      dragging: true,
      moved: false,
      startX: clientX,
      startY: clientY,
      origX: rect.x,
      origY: rect.y,
    };
  };
  const onPointerDown = (e) => {
    // Capture the pointer so the drag keeps tracking once the finger/cursor
    // leaves the small FAB — without this, dragging barely worked on phones.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    beginDrag(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    const s = dragState.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (Math.abs(dx) + Math.abs(dy) > 4) s.moved = true;
    // Clamp inside the viewport (56 px FAB size)
    const maxX = window.innerWidth - 60;
    const maxY = window.innerHeight - 60;
    const nx = Math.min(Math.max(4, s.origX + dx), maxX);
    const ny = Math.min(Math.max(4, s.origY + dy), maxY);
    setPos({ x: nx, y: ny });
  };
  const onPointerUp = () => {
    const s = dragState.current;
    if (!s.dragging) return;
    s.dragging = false;
    if (s.moved && pos) {
      try {
        window.localStorage.setItem(POS_KEY, JSON.stringify(pos));
      } catch (_e) { /* private-mode */ }
    }
  };
  // Suppress the click that fires after a drag so the panel doesn't open
  // when the user is just repositioning the icon.
  const onIconClick = (e) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.moved = false;
      return;
    }
    setOpen(true);
  };

  // Seed the greeting the first time the panel opens.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          text: t(
            'مرحباً! أنا مساعد طبابا. اسألني عن الأطباء المتاحين الآن أو باقات الاشتراك.',
            'Hi! I am Tababa\'s assistant. Ask me who is online right now, about a specific doctor, or about our plans.',
            "Bonjour ! Je suis l'assistant Tababa. Demandez-moi qui est en ligne."
          ),
        },
      ]);
    }
  }, [open, messages.length, t]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;
    setMessages((m) => [...m, { role: 'user', text: trimmed }]);
    setInput('');
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/ai/assistant`,
        {
          message: trimmed,
          session_id: sessionIdRef.current,
          language,
        },
        { withCredentials: true }
      );
      if (r.data?.session_id) {
        sessionIdRef.current = r.data.session_id;
        try {
          window.localStorage.setItem(SESSION_KEY, r.data.session_id);
        } catch (_e) { /* private-mode fallback */ }
      }
      setMessages((m) => [...m, { role: 'assistant', text: r.data?.reply || '…' }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          text: t(
            'عذراً، حدث خطأ. حاول مرة أخرى.',
            'Sorry, something went wrong. Please try again.',
            'Désolé, une erreur est survenue.'
          ),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Floating icon — draggable. Position persists in localStorage. */}
      {!open && (
        <button
          onClick={onIconClick}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="ai-bot-open"
          style={
            pos
              ? { left: `${pos.x}px`, top: `${pos.y}px`, touchAction: 'none' }
              : { touchAction: 'none' }
          }
          className="fixed z-[850] bottom-24 md:bottom-6 end-4 md:end-6 w-14 h-14 rounded-full bg-[#1e88e5] text-white shadow-2xl hover:bg-[#1976d2] flex items-center justify-center cursor-grab active:cursor-grabbing select-none"
          aria-label={t('افتح المساعد الذكي (اسحب لتحريكه)', 'Open AI assistant (drag to move)', "Ouvrir l'assistant IA (glisser pour déplacer)")}
        >
          <MessageCircle size={26} />
          <span className="absolute -top-1 -end-1 w-3 h-3 rounded-full bg-green-400 border-2 border-white pointer-events-none" />
        </button>
      )}

      {/* Panel — anchors near the FAB when it's been dragged; otherwise
          uses the default corner slot. */}
      {open && (
        <div
          data-testid="ai-bot-panel"
          style={
            pos
              ? {
                  // Anchor the panel above the FAB, clamped inside the viewport
                  left: `${Math.min(Math.max(8, pos.x - 180), (typeof window !== 'undefined' ? window.innerWidth : 400) - 380)}px`,
                  top: `${Math.max(8, pos.y - 460)}px`,
                  height: 'min(70dvh, 600px)',
                }
              : { height: 'min(70dvh, 600px)' }
          }
          className="fixed z-[850] bottom-24 md:bottom-6 end-4 md:end-6 w-[92vw] max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1e88e5] to-[#1565c0] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Bot size={20} />
              </div>
              <div>
                <p className="font-bold text-sm" data-testid="ai-bot-title">
                  {t('مساعد طبابا', 'Tababa Assistant', 'Assistant Tababa')}
                </p>
                <p className="text-xs opacity-80">
                  {t('متصل الآن', 'Online now', 'En ligne')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="ai-bot-close"
              className="hover:bg-white/20 rounded-full p-1.5"
              aria-label={t('إغلاق', 'Close', 'Fermer')}
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50"
            data-testid="ai-bot-messages"
          >
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-[#1e88e5] text-white rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <div className="whitespace-pre-wrap leading-relaxed" data-testid={`ai-msg-${i}`}>
                      {m.text}
                    </div>
                  ) : (
                    m.text
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="ai-bot-loading">
                <div className="bg-white text-gray-500 rounded-2xl px-4 py-2 text-sm flex items-center gap-2 border border-gray-200">
                  <Loader2 size={14} className="animate-spin" />
                  {t('يكتب...', 'Typing…', 'En train d\'écrire…')}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={t('اكتب سؤالك...', 'Type your question…', 'Votre question…')}
              className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#1e88e5]"
              data-testid="ai-bot-input"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              className="w-10 h-10 rounded-xl bg-[#1e88e5] hover:bg-[#1976d2] text-white flex items-center justify-center disabled:opacity-50"
              data-testid="ai-bot-send"
              aria-label={t('إرسال', 'Send', 'Envoyer')}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIBotWidget;
